import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { uniq, partition } from 'lodash-es';
import {
  bool, func, oneOfType, string, number
} from 'prop-types';

import { withLang } from '../../../i18n';
import useSurveyParticipantsInput from '../useSurveyParticipantsInput';
import Notification from '../../Notification';
import {
  participantErrors, participantWarnings, surveyFieldIds, courseUnitRealisationWarnings
} from '../../../constants';
import { notificationType } from '../../../constants/types';
import {
  addCourseUnitRealisationsAction,
  addManualSurveyParticipantsAction,
  removeCourseUnitRealisationAction,
  selectParticipantEmailAction
} from '../../../actions';
import IsRequiredField from '../../IsRequiredField';
import ParticipantList from '../ParticipantList';
import CourseList from '../CourseList';
import HelpButton from '../../HelpButton';
import styles from '../surveyParticipantsEditor.module.css';

const FIXABLE_ERRORS = [
  participantErrors.NO_EMAIL,
  participantErrors.DUPLICATE_EMAIL,
  participantErrors.INVALID_EMAIL
];

const countUniqueParticipants = (participants, courses) =>
  uniq([
    ...participants.map((participant) => participant.studentNumber),
    ...courses.reduce(
      (acc, course) => [...acc, ...course.students.map((student) => student.studentNumber)],
      []
    )
  ]).length;

const annotateCourses = (courses) => {
  const annotateCourse = (course) => {
    const warnings = course.warnings ? course.warnings : [];
    if (!course.courseName) {
      warnings.push(courseUnitRealisationWarnings.MISSING_COURSE_NAME);
    }
    const students = course.students.filter((student) => {
      if (student.error && student.error.code === 422) {
        warnings.push(courseUnitRealisationWarnings.MISSING_EPPNS);
      }
      // Return false to filter out students with any error property
      return !student.error;
    });
    if (students.some((student) => (!student.email))) {
      warnings.push(courseUnitRealisationWarnings.MISSING_EMAILS);
    }

    return { ...course, students, warnings };
  };
  return courses.map(annotateCourse);
};

const annotateParticipants = (participants, existingParticipants) => {
  const participantsEmails = participants.map((p) => p.email);
  const oldParticipantIds = existingParticipants.map((p) => p.externalId);

  const annotateParticipant = (participant, i) => {
    let { error, warning } = participant;
    if (!participant.name) {
      error = participantErrors.NOT_FOUND;
    } else if (!participant.email) {
      error = participantErrors.NO_EMAIL;
    } else if (participantsEmails.lastIndexOf(participant.email) !== i) {
      error = participantErrors.DUPLICATE_EMAIL;
    } else if (!error && oldParticipantIds.includes(participant.externalId)) {
      warning = participantWarnings.ALREADY_IN_SURVEY;
    }
    return { ...participant, error, warning };
  };

  return [...participants.map(annotateParticipant), ...existingParticipants];
};

const StudentParticipantsEditor = ({
  translate: t, surveyId, showExistingParticipants, showAsRequired, showProblems, isInvalid, surveyType
}) => {
  const dispatch = useDispatch();

  const courses = useSelector((state) => (state.survey && state.survey.surveyOptions.courses) || []);
  const participants = useSelector((state) => state.survey.surveyOptions.participants);

  const {
    inputValue, inputRows, handleChange, resetInput, splitInputValue
  } = useSurveyParticipantsInput();
  const [notAddedParticipants, setNotAddedParticipants] = useState([]);

  // Memoize to avoid recalculating on every render.
  const existingParticipants = useMemo(() => participants.filter((p) => !p.new), [participants]);
  const newParticipants = useMemo(() => participants.filter((p) => p.new), [participants]);
  const annotatedParticipants = useMemo(() =>
    annotateParticipants(newParticipants, existingParticipants),
  [newParticipants, existingParticipants]);
  const annotatedCourses = useMemo(() => annotateCourses(courses), [courses]);
  const totalParticipantCount = useMemo(
    () => countUniqueParticipants(participants, annotatedCourses),
    [participants, annotatedCourses]
  );

  const handleSelectParticipantEmail = useCallback(
    (studentNumber, email) => dispatch(selectParticipantEmailAction(studentNumber, email)),
    [dispatch]
  );

  const onParticipantsSubmit = useCallback(async (ids) => {
    const currentStudentNumbers = participants.map((p) => p.studentNumber);
    const courseUnitRealisationsResult = await dispatch(
      addCourseUnitRealisationsAction(ids, surveyId)
    );
    const courseUnitRealisations = courseUnitRealisationsResult.value;

    const courseStudents = courseUnitRealisations
      .filter((course) => course.result)
      .reduce(
        (acc, current) => [...acc, ...current.result.students],
        []
      );

    const checkIsAlreadyAddedStudentNumber = (studentNumber) =>
      currentStudentNumbers.includes(studentNumber);

    const alreadyAddedStudentsInCourses = courseStudents.filter((student) =>
      checkIsAlreadyAddedStudentNumber(student.studentNumber));

    const studentNumbersToAdd = courseUnitRealisations
      .filter((course) => course.error)
      .map((c) => c.courseId);

    const [alreadyAddedStudentNumbers, newStudentNumbers] =
       partition(studentNumbersToAdd, checkIsAlreadyAddedStudentNumber);

    const notAdded = [
      ...alreadyAddedStudentsInCourses.map((p) => p.studentNumber),
      ...alreadyAddedStudentNumbers
    ];

    if (newStudentNumbers.length > 0) {
      dispatch(addManualSurveyParticipantsAction(newStudentNumbers));
    }
    setNotAddedParticipants(notAdded);
  }, [participants, surveyId, dispatch]);

  const processParticipants = async (event) => {
    event.preventDefault();
    const ids = splitInputValue(inputValue);
    await onParticipantsSubmit(ids);
    resetInput();
  };

  const renderMessages = useCallback(() => {
    const checkRequiresAction = (participant) => (
      (participant.error && FIXABLE_ERRORS.includes(participant.error))
     || participant.warning === participantWarnings.CHANGING_EMAIL
    );
    const checkNotFound = (participant) =>
      (participant.error === participantErrors.NOT_FOUND);
    const checkAlreadyInSurvey = (participant) =>
      (participant.warning === participantWarnings.ALREADY_IN_SURVEY);
    const checkAlreadyAdded = (participant) =>
      notAddedParticipants.includes(participant.studentNumber);
    const checkMissingCourseName = (course) =>
      course.warnings.includes(courseUnitRealisationWarnings.MISSING_COURSE_NAME);
    const checkMissingEmails = (course) =>
      course.warnings.includes(courseUnitRealisationWarnings.MISSING_EMAILS);
    const checkMissingEppns = (course) =>
      course.warnings.includes(courseUnitRealisationWarnings.MISSING_EPPNS);

    const addedCount = countUniqueParticipants(
      annotatedParticipants.filter((p) => !(p.error || p.warning) && p.new),
      annotatedCourses.filter((c) => c.new)
    );
    const requiresActionCount = annotatedParticipants.filter(checkRequiresAction).length;
    const noContentCount = annotatedParticipants.filter(checkNotFound).length;
    const alreadyAddedCount = annotatedParticipants.filter(checkAlreadyInSurvey).length;
    const existingParticipantsCount = existingParticipants.length;
    const notAddedCount = participants.filter(checkAlreadyAdded).length;
    const missingCourseNameCount = annotatedCourses.filter(checkMissingCourseName).length;
    const missingEmails = annotatedCourses.some(checkMissingEmails);
    const missingEppns = annotatedCourses.some(checkMissingEppns);

    const getNotificationWithAmount = (amount, pluralTextKey, textKey, type) => {
      if (!amount) return null;
      return <Notification type={type} text={`${amount} ${t(`surveyParticipantsEditor.${amount > 1 ? pluralTextKey : textKey}`)}`} />;
    };

    return (
      <div>
        {showExistingParticipants
         && getNotificationWithAmount(existingParticipantsCount, 'surveyHasExistingParticipants', 'surveyHasExistingParticipant')}
        {getNotificationWithAmount(addedCount, 'studentsAdded', 'studentAdded', notificationType.OK)}
        {getNotificationWithAmount(notAddedCount, 'participantsAlreadyAdded', 'participantAlreadyAdded', notificationType.OK)}
        {getNotificationWithAmount(alreadyAddedCount, 'studentsAlreadyAdded', 'studentAlreadyAdded', notificationType.OK)}
        {getNotificationWithAmount(requiresActionCount, 'requiresActionPl', 'requiresActionSg', notificationType.WARNING)}
        {getNotificationWithAmount(noContentCount, 'studentsRejected', 'studentRejected', notificationType.ERROR)}
        {getNotificationWithAmount(missingCourseNameCount, 'missingCourseNames', 'missingCourseName', notificationType.WARNING)}
        {missingEmails && <Notification type={notificationType.WARNING} text={t('surveyParticipantsEditor.missingEmails')} />}
        {missingEppns && <Notification type={notificationType.WARNING} text={t('surveyParticipantsEditor.missingEppns')} />}
      </div>
    );
  }, [notAddedParticipants, annotateParticipants, annotateCourses, existingParticipants, participants, showExistingParticipants, t]);

  const editorContainerClass = showProblems && isInvalid ? `${styles.formItem} formError` : styles.formItem;
  const showDescription = participants.length === 0;

  const noBottomMargin = { marginBottom: 0 };
  return (
    <div>
      <div className={editorContainerClass}>
        <label htmlFor={surveyFieldIds.PARTICIPANTS_INPUT}>
          {t('surveyParticipantsEditor.participantsSearchTitle')}
          {showAsRequired && <IsRequiredField />}
          <HelpButton content={t('surveyParticipantsEditor.courseRealisationIdHelp')} />
        </label>
        <textarea
          id={surveyFieldIds.PARTICIPANTS_INPUT}
          placeholder={t('surveyParticipantsEditor.addParticipantPlaceholder')}
          value={inputValue}
          onChange={handleChange}
          rows={inputRows}
          data-cy="participants-text-area"
        />
        <div className={showDescription ? styles.addButtonContainerDescription : styles.addButtonContainer}>
          {showDescription
          && <p className={styles.addParticipantsDescription}>{t('surveyParticipantsEditor.addParticipantDescription')}</p>}
          <button
            type="button"
            id="addParticipantsButton"
            className={`button--action ${styles.addButton}`}
            onClick={processParticipants}
            data-cy="add-participants-button"
          >
            {t('common.add')}
          </button>
        </div>
        <div className={styles.description}>
          {renderMessages()}
        </div>
      </div>
      <CourseList
        courses={annotatedCourses}
        removeCourse={(courseUnitId) =>
          dispatch(removeCourseUnitRealisationAction(courseUnitId, surveyType, surveyId))}
        surveyType={surveyType}
        surveyId={surveyId}
      />
      <ParticipantList
        count={annotatedParticipants.length}
        surveyType={surveyType}
        participants={annotatedParticipants}
        selectParticipantEmail={handleSelectParticipantEmail}
      />
      <p style={noBottomMargin}>{t('surveyParticipantsEditor.totalParticipants', { totalParticipantCount })}</p>
    </div>
  );
};

StudentParticipantsEditor.propTypes = {
  surveyId: oneOfType([string, number]),
  showExistingParticipants: bool,
  showAsRequired: bool.isRequired,
  showProblems: bool,
  isInvalid: bool,
  surveyType: string.isRequired,
  translate: func.isRequired
};

StudentParticipantsEditor.defaultProps = {
  surveyId: null,
  showProblems: false,
  isInvalid: false,
  showExistingParticipants: false
};

export default withLang(StudentParticipantsEditor);
